// components/DistribucionHabitacionesModal.js
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Button,
  Form,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Card,
  Statistic,
  Badge,
  Spin,
  Descriptions,
  InputNumber,
  Divider,
  Alert
} from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  ReloadOutlined,
  HomeOutlined,
  TeamOutlined,
  SwapOutlined,
  PlusOutlined,
  MinusOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { BASE_URL } from '../utils/constants';

const { Text } = Typography;
const { Option } = Select;

const DistribucionHabitacionesModal = ({ 
  visible, 
  onCancel, 
  numReserva, 
  onSuccess,
  readOnly = false 
}) => {
  // Estados
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [distribucionActual, setDistribucionActual] = useState([]);
  const [distribucionNueva, setDistribucionNueva] = useState([]);
  const [habitacionesVenta, setHabitacionesVenta] = useState([]);
  const [reservaInfo, setReservaInfo] = useState(null);
  const [cambios, setCambios] = useState([]);
  const [habitacionesDropdown, setHabitacionesDropdown] = useState([]);
  const [editingRow, setEditingRow] = useState(null);

  // Cargar datos cuando se abre el modal
  useEffect(() => {
    if (visible && numReserva) {
      cargarDatosIniciales();
    }
  }, [visible, numReserva]);

  // Resetear estado cuando se cierra el modal
  useEffect(() => {
    if (!visible) {
      setDistribucionActual([]);
      setDistribucionNueva([]);
      setReservaInfo(null);
      setCambios([]);
      setEditingRow(null);
    }
  }, [visible]);

  // Calcular cambios cuando cambia la distribución nueva
  useEffect(() => {
    if (distribucionNueva.length > 0) {
      calcularCambios();
    }
  }, [distribucionNueva]);

  const cargarDatosIniciales = async () => {
    if (!numReserva) {
      message.error('Se debe indicar un número de reserva');
      onCancel();
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Cargar información de la reserva
      const infoReserva = await axios.get(
        `${BASE_URL}/api/distribucion-habitaciones/reserva/${numReserva}/info`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (infoReserva.data.success) {
        setReservaInfo(infoReserva.data.data);
        
        // Cargar distribución actual
        const distribucionActualRes = await axios.get(
          `${BASE_URL}/api/distribucion-habitaciones/reserva/${numReserva}/distribucion-actual`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // console.log(" distribucionActualRes.data.data => ",distribucionActualRes.data.data);
        
        const distribucionData = distribucionActualRes.data.data.map(item => ({
          ...item,
          key: `${item.HabitacionVenta2}_${item.Pax}`,
          HabitacionVenta2: item.HabitacionVenta2 || '',
          descripcion: item.descripcion || '',
          Habs: item.Habs || 0,
          Pax: item.Pax || 1
        }));
        
        setDistribucionActual(distribucionData);
        setDistribucionNueva(JSON.parse(JSON.stringify(distribucionData)));
        
        // Cargar habitaciones de venta
        if (infoReserva.data.data.CodigoHotel) {
          const habitacionesRes = await axios.get(
            `${BASE_URL}/api/distribucion-habitaciones/hotel/${infoReserva.data.data.CodigoHotel}/habitaciones-venta`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          setHabitacionesVenta(habitacionesRes.data.data);
          
          // Cargar dropdown
          const dropdownRes = await axios.get(
            `${BASE_URL}/api/distribucion-habitaciones/hotel/${infoReserva.data.data.CodigoHotel}/habitaciones-dropdown`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          // console.log(" dropdownRes.data.data => ",dropdownRes.data.data);
          
          setHabitacionesDropdown(dropdownRes.data.data);
        }
      }
      
    } catch (error) {
      console.error('❌ Error cargando datos:', error);
      message.error('Error al cargar los datos de la reserva');
    } finally {
      setLoading(false);
    }
  };

  const calcularCambios = () => {
    const cambiosCalculados = [];
    const todasClaves = new Set([
      ...distribucionActual.map(d => `${d.HabitacionVenta2}_${d.Pax}`),
      ...distribucionNueva.map(d => `${d.HabitacionVenta2}_${d.Pax}`)
    ]);
    
    todasClaves.forEach(clave => {
      const [codigo, pax] = clave.split('_');
      const actualItem = distribucionActual.find(
        d => d.HabitacionVenta2 === codigo && d.Pax === parseInt(pax)
      );
      const nuevoItem = distribucionNueva.find(
        d => d.HabitacionVenta2 === codigo && d.Pax === parseInt(pax)
      );
      
      const valorViejo = actualItem?.Habs || 0;
      const valorNuevo = nuevoItem?.Habs || 0;
      
      if (valorViejo !== valorNuevo) {
        cambiosCalculados.push({
          codigo,
          Pax: parseInt(pax),
          valorViejo,
          valorNuevo,
          Descripcion: nuevoItem?.descripcion || actualItem?.descripcion,
          diferencia: valorNuevo - valorViejo
        });
      }
    });
    
    setCambios(cambiosCalculados);
  };

  const handleRowChange = (record, field, value) => {
    if (readOnly) return;

    console.log(record,field,value);
    
    
    const newData = [...distribucionNueva];
    const index = newData.findIndex(
      item => item.HabitacionVenta2 === record.HabitacionVenta2 && item.Pax === record.Pax
    );
    
    if (index > -1) {
      if (field === 'HabitacionVenta2') {
        const habitacionSel = habitacionesVenta.find(h => h.Codigo === value);
        newData[index] = {
          ...newData[index],
          HabitacionVenta2: value,
          Descripcion: habitacionSel?.Descripcion || '',
          Pax: habitacionSel?.Pax || record.Pax
        };
      } else if (field === 'Habs') {
        newData[index][field] = parseInt(value) || 0;
      }
      
      setDistribucionNueva(newData);
    }
  };

  const handleAddRow = () => {
    if (readOnly) return;
    
    const newRow = {
      key: `new_${Date.now()}`,
      HabitacionVenta2: '',
      Descripcion: '',
      Habs: 1,
      Pax: 1,
      isNew: true
    };
    setDistribucionNueva([...distribucionNueva, newRow]);
    setEditingRow(newRow.key);
  };

  const handleRemoveRow = (record) => {
    if (readOnly) return;
    
    if (record.isNew) {
      setDistribucionNueva(distribucionNueva.filter(
        item => item.key !== record.key
      ));
    } else {
      // Para filas existentes, simplemente poner habs a 0
      const newData = [...distribucionNueva];
      const index = newData.findIndex(
        item => item.HabitacionVenta2 === record.HabitacionVenta2 && item.Pax === record.Pax
      );
      if (index > -1) {
        newData[index].Habs = 0;
        setDistribucionNueva(newData);
      }
    }
  };

  const handleGuardar = async () => {
    if (cambios.length === 0) {
      message.info('No hay cambios para guardar');
      return;
    }
    console.log(" cambios => ",cambios);
    
    Modal.confirm({
      title: '¿Guardar cambios?',
      icon: <WarningOutlined />,
      content: (
        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
          <p>Se van a aplicar los siguientes cambios:</p>
          {cambios.map((cambio, index) => (
            <div key={index} style={{ marginBottom: 8 }}>
              <Tag color={cambio.diferencia > 0 ? 'green' : 'red'}>
                {cambio.descripcion} ({cambio.pax} pax):
              </Tag>
              <Text strong>
                {cambio.valorViejo} → {cambio.valorNuevo}
              </Text>
              <Badge 
                count={cambio.diferencia > 0 ? `+${cambio.diferencia}` : cambio.diferencia}
                style={{ 
                  backgroundColor: cambio.diferencia > 0 ? '#52c41a' : '#ff4d4f',
                  marginLeft: 8
                }}
              />
            </div>
          ))}
        </div>
      ),
      onOk: async () => {
        setSaving(true);
        try {
          const token = localStorage.getItem('token');
          
          const response = await axios.post(
            `${BASE_URL}/api/distribucion-habitaciones/reserva/${numReserva}/distribucion`,
            {
              distribucionActual,
              nuevaDistribucion: distribucionNueva.filter(d => d.Habs > 0)
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.data.success) {
            message.success('Distribución actualizada correctamente');
            if (onSuccess) {
              onSuccess(response.data.data);
            }
            onCancel();
          }
        } catch (error) {
          console.error('❌ Error guardando:', error);
          message.error(error.response?.data?.error || 'Error al guardar los cambios');
        } finally {
          setSaving(false);
        }
      }
    });
  };

  const columnas = [
    {
      title: 'Tipo Habitación',
      dataIndex: 'HabitacionVenta2',
      key: 'HabitacionVenta2',
      width: 280,
      render: (value, record) => (
        !readOnly && (record.isNew || editingRow === record.key) ? (
          <Select
            value={value}
            onChange={(val) => handleRowChange(record, 'HabitacionVenta2', val)}
            style={{ width: '100%' }}
            placeholder="Seleccionar habitación"
            showSearch
            optionFilterProp="children"
            size="small"
          >
            {habitacionesDropdown.map(hab => (
              <Option key={hab.value} value={hab.value}>
                <Space>
                  <HomeOutlined />
                  <Tag color="blue">{hab.value}</Tag>
                  <span>{hab.label.split(' - ')[1]}</span>
                  <Tag color="purple">
                    <TeamOutlined /> {hab.Pax}
                  </Tag>
                </Space>
              </Option>
            ))}
          </Select>
        ) : (
          <Space>
            <Tag color="blue" style={{ fontWeight: 'bold' }}>
              {value}
            </Tag>
            <Text>{record.Descripcion}</Text>
          </Space>
        )
      )
    },
    {
      title: 'Pax',
      dataIndex: 'Pax',
      key: 'Pax',
      width: 70,
      align: 'center',
      render: (Pax) => (
        <Tag color="purple">
          <TeamOutlined /> {Pax}
        </Tag>
      )
    },
    {
      title: 'Actual',
      dataIndex: 'Habs',
      key: 'habsActual',
      width: 70,
      align: 'center',
      render: (_, record) => {
        const actual = distribucionActual.find(
          d => d.HabitacionVenta2 === record.HabitacionVenta2 && d.Pax === record.Pax
        );
        return (
          <Badge 
            count={actual?.Habs || 0}
            style={{ backgroundColor: '#1890ff' }}
          />
        );
      }
    },
    {
      title: 'Nuevo',
      dataIndex: 'Habs',
      key: 'Habs',
      width: 90,
      align: 'center',
      render: (value, record) => (
        <InputNumber
          min={0}
          max={99}
          value={value}
          onChange={(val) => handleRowChange(record, 'Habs', val)}
          style={{ width: 70 }}
          disabled={readOnly || !record.HabitacionVenta2}
          size="small"
        />
      )
    },
    {
      title: 'Diff',
      key: 'diferencia',
      width: 70,
      align: 'center',
      render: (_, record) => {
        const actual = distribucionActual.find(
          d => d.HabitacionVenta2 === record.HabitacionVenta2 && d.Pax === record.Pax
        );
        const diff = (record.Habs || 0) - (actual?.Habs || 0);
        if (diff === 0) return <Text type="secondary">-</Text>;
        return (
          <Tag color={diff > 0 ? 'green' : 'red'}>
            {diff > 0 ? `+${diff}` : diff}
          </Tag>
        );
      }
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 80,
      align: 'center',
      render: (_, record) => (
        !readOnly && (
          <Tooltip title="Eliminar">
            <Button
              danger
              icon={<MinusOutlined />}
              size="small"
              onClick={() => handleRemoveRow(record)}
              disabled={!record.HabitacionVenta2 || record.Habs === 0}
            />
          </Tooltip>
        )
      )
    }
  ];

  const resumenCambios = (
    <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
      <Row gutter={8}>
        <Col span={6}>
          <Statistic 
            title="Cambios" 
            value={cambios.length}
            prefix={<SwapOutlined />}
            valueStyle={{ color: '#1890ff', fontSize: 20 }}
          />
        </Col>
        <Col span={6}>
          <Statistic 
            title="Añadir" 
            value={cambios.filter(c => c.diferencia > 0).reduce((acc, c) => acc + c.diferencia, 0)}
            prefix={<PlusOutlined />}
            valueStyle={{ color: '#52c41a', fontSize: 20 }}
          />
        </Col>
        <Col span={6}>
          <Statistic 
            title="Eliminar" 
            value={cambios.filter(c => c.diferencia < 0).reduce((acc, c) => acc + Math.abs(c.diferencia), 0)}
            prefix={<MinusOutlined />}
            valueStyle={{ color: '#ff4d4f', fontSize: 20 }}
          />
        </Col>
        <Col span={6}>
          <Statistic 
            title="Total" 
            value={distribucionNueva.reduce((acc, d) => acc + (d.Habs || 0), 0)}
            prefix={<HomeOutlined />}
            valueStyle={{ fontSize: 20 }}
          />
        </Col>
      </Row>
    </Card>
  );

  return (
    <Modal
      title={
        <Space>
          <HomeOutlined style={{ color: '#1890ff' }} />
          <span>Distribución de Habitaciones - Reserva {numReserva}</span>
          {reservaInfo && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {reservaInfo.HotelNombre}
            </Tag>
          )}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={1000}
      style={{ top: 20 }}
      footer={
        <Space>
          <Button 
            icon={<CloseOutlined />} 
            onClick={onCancel}
          >
            Cancelar
          </Button>
          {!readOnly && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleGuardar}
              loading={saving}
              disabled={cambios.length === 0}
            >
              Guardar Cambios
            </Button>
          )}
        </Space>
      }
    >
      <Spin spinning={loading}>
        {!numReserva ? (
          <Alert
            message="Error"
            description="No se ha especificado un número de reserva"
            type="error"
            showIcon
          />
        ) : (
          <>
            {/* Información de la reserva */}
            {reservaInfo && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <Descriptions size="small" column={2}>
                  <Descriptions.Item label="Cliente">
                    <Text strong>{reservaInfo.NombreCliente}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Fechas">
                    {new Date(reservaInfo.FechaEntrada).toLocaleDateString()} - {new Date(reservaInfo.FechaSalida).toLocaleDateString()}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {/* Resumen de cambios */}
            {resumenCambios}

            {/* Tabla de distribución */}
            <Card size="small">
              {!readOnly && (
                <div style={{ marginBottom: 16 }}>
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={handleAddRow}
                    size="small"
                  >
                    Añadir Tipo Habitación
                  </Button>
                  <Tooltip title="Los cambios se muestran en tiempo real">
                    <InfoCircleOutlined style={{ color: '#999', marginLeft: 8 }} />
                  </Tooltip>
                </div>
              )}

              <Table
                columns={columnas}
                dataSource={distribucionNueva}
                rowKey={(record) => record.key || `${record.HabitacionVenta2}_${record.Pax}`}
                pagination={false}
                scroll={{ y: 300 }}
                size="small"
                locale={{
                  emptyText: 'No hay habitaciones configuradas'
                }}
                footer={() => (
                  <div style={{ textAlign: 'right' }}>
                    <Text type="secondary">
                      Total: {distribucionNueva.reduce((acc, d) => acc + (d.Habs || 0), 0)} habitaciones
                    </Text>
                  </div>
                )}
              />
            </Card>

            {/* Lista de cambios pendientes */}
            {cambios.length > 0 && !readOnly && (
              <Card style={{ marginTop: 16, backgroundColor: '#fff7e6' }} size="small">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <WarningOutlined style={{ color: '#faad14' }} />
                  <Text strong>Cambios pendientes:</Text>
                </div>
                <div style={{ maxHeight: 100, overflowY: 'auto' }}>
                  {cambios.map((cambio, index) => (
                    <Tag 
                      key={index} 
                      color={cambio.diferencia > 0 ? 'green' : 'red'}
                      style={{ marginBottom: 4 }}
                    >
                      {cambio.descripcion} ({cambio.Pax}p): {cambio.valorViejo} → {cambio.valorNuevo}
                      {cambio.diferencia > 0 ? ` (+${cambio.diferencia})` : ` (${cambio.diferencia})`}
                    </Tag>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default DistribucionHabitacionesModal;